import { keyframes } from '@emotion/react';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReportIcon from '@mui/icons-material/Report';
import { Box, Button, Card, CardContent, Container, TextField, Typography, alpha, styled } from '@mui/material';
import * as Sentry from '@sentry/react';
import { useEffect, useRef, useState } from 'react';
import { FallbackProps } from 'react-error-boundary';
import { safeSessionStorage } from 'utils/safeStorage';

const bounce = keyframes`
    0%, 20%, 53%, 80%, 100% { transform: translate3d(0, 0, 0); }
    40%, 43% { transform: translate3d(0, -15px, 0); }
    70% { transform: translate3d(0, -7px, 0); }
    90% { transform: translate3d(0, -3px, 0); }
`;

const shake = keyframes`
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
    20%, 40%, 60%, 80% { transform: translateX(2px); }
`;

const ErrorContainer = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${alpha(theme.palette.primary.light, 0.1)} 100%)`,
    padding: theme.spacing(2),
}));

const ErrorCard = styled(Card)(({ theme }) => ({
    maxWidth: 600,
    width: '100%',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    border: `1px solid ${theme.palette.grey[200]}`,
    borderRadius: theme.spacing(3),
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
    },
}));

const ErrorIconContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(3),
}));

const ErrorCode = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.grey[100],
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.spacing(1.5),
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    maxHeight: 150,
    overflowY: 'auto',
    wordBreak: 'break-word',
    position: 'relative',
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
    justifyContent: 'center',
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        '& .MuiButton-root': {
            width: '100%',
        },
    },
}));

const FeedbackSection = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(3),
    padding: theme.spacing(3),
    backgroundColor: alpha(theme.palette.primary.light, 0.05),
    borderRadius: theme.spacing(2),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    transition: 'all 0.3s ease',
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.light, 0.08),
        transform: 'translateY(-1px)',
    },
}));

const FunButton = styled(Button)(({ theme }) => ({
    borderRadius: theme.spacing(3),
    textTransform: 'none',
    fontWeight: 600,
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
    },
}));

export const GenericError = ({ message }: { message: string }) => {
    const [showFeedback, setShowFeedback] = useState(false);
    const [feedback, setFeedback] = useState('');
    const [email, setEmail] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [countdown, setCountdown] = useState(5);

    // Fun error messages
    const funMessages = [
        'Oops! Something decided to take a coffee break',
        'Houston, we have a problem!',
        'Looks like our code got a little too creative',
        'Well, this is awkward...',
        'Our servers are having a moment',
    ];

    // Store random message in state so it doesn't change on re-renders
    const [randomFunMessage] = useState(() => funMessages[Math.floor(Math.random() * funMessages.length)]);

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (submitted && countdown > 0) {
            timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        }
        if (submitted && countdown === 0) {
            window.location.reload();
        }
        return () => clearTimeout(timer);
    }, [submitted, countdown]);

    const handleSubmitFeedback = () => {
        Sentry.captureMessage('User Feedback', {
            extra: {
                feedback,
                email,
                userFeedback: true,
            },
        });
        setSubmitted(true);
    };

    return (
        <ErrorContainer>
            <Container maxWidth="sm">
                <ErrorCard>
                    <CardContent sx={{ p: 4, '&:last-child': { pb: 4 } }}>
                        <ErrorIconContainer>
                            <ReportIcon
                                sx={{
                                    fontSize: 72,
                                    color: 'error.main',
                                    animation: `${bounce} 2s ease-in-out infinite`,
                                }}
                            />
                        </ErrorIconContainer>

                        <Typography
                            variant="h4"
                            align="center"
                            sx={{
                                fontWeight: 700,
                                color: 'text.primary',
                                mb: 1,
                            }}
                        >
                            {randomFunMessage}
                        </Typography>

                        <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 2, fontWeight: 500 }}>
                            Don't worry, it's not you... it's us!
                        </Typography>

                        <Typography
                            variant="body1"
                            align="center"
                            color="text.secondary"
                            sx={{ mb: 3, lineHeight: 1.6 }}
                        >
                            Something unexpected happened while we were processing your request. Our team of digital
                            detectives are probably already on the case!
                        </Typography>

                        {message && (
                            <ErrorCode
                                sx={{
                                    '&:hover': {
                                        animation: `${shake} 0.5s ease-in-out`,
                                    },
                                }}
                            >
                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                    Technical Details (for the curious):
                                </Typography>
                                {message}
                            </ErrorCode>
                        )}

                        {submitted ? (
                            <Box sx={{ textAlign: 'center', mt: 3 }}>
                                <Typography variant="h6" color="success.main" sx={{ mb: 1 }}>
                                    Thanks for helping us improve!
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Taking you back in {countdown} second{countdown !== 1 ? 's' : ''}...
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <ButtonContainer>
                                    <FunButton
                                        variant="contained"
                                        size="large"
                                        startIcon={<RefreshIcon />}
                                        onClick={() => window.location.reload()}
                                        sx={{ px: 4 }}
                                    >
                                        Try Again
                                    </FunButton>
                                    <FunButton
                                        variant="outlined"
                                        size="large"
                                        onClick={() => setShowFeedback(!showFeedback)}
                                        sx={{ px: 4 }}
                                    >
                                        {showFeedback ? 'Hide Form' : 'Tell Us What Happened'}
                                    </FunButton>
                                </ButtonContainer>

                                {showFeedback && (
                                    <FeedbackSection>
                                        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                                            Help us be better
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                            We'd love to know what you were up to when this happened. Your feedback
                                            helps us squash bugs faster than a game of whack-a-mole!
                                        </Typography>

                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={4}
                                            label="What were you doing?"
                                            placeholder="I was just trying to... and then everything went kaput!"
                                            value={feedback}
                                            onChange={e => setFeedback(e.target.value)}
                                            variant="outlined"
                                            sx={{
                                                mb: 2,
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 2,
                                                },
                                            }}
                                        />

                                        <TextField
                                            fullWidth
                                            label="Email (so we can high-five you later)"
                                            placeholder="your.awesome@email.com"
                                            value={email}
                                            onChange={e => setEmail(e.target.value)}
                                            variant="outlined"
                                            sx={{
                                                mb: 3,
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 2,
                                                },
                                            }}
                                        />

                                        <FunButton
                                            fullWidth
                                            variant="contained"
                                            size="large"
                                            onClick={handleSubmitFeedback}
                                            disabled={!feedback.trim()}
                                            sx={{
                                                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                                                '&:hover': {
                                                    background: 'linear-gradient(45deg, #1976D2 30%, #0288D1 90%)',
                                                },
                                            }}
                                        >
                                            Send Feedback
                                        </FunButton>
                                    </FeedbackSection>
                                )}
                            </>
                        )}
                    </CardContent>
                </ErrorCard>
            </Container>
        </ErrorContainer>
    );
};

const CHUNK_LOAD_PATTERNS = [
    /importing a module script failed/i,
    /failed to fetch dynamically imported module/i,
    /unable to preload css/i,
    /is not a valid javascript mime type/i,
];

export const CHUNK_RELOAD_KEY = 'chunk_load_reloaded';
const MAX_CHUNK_RELOAD_ATTEMPTS = 2;
// Netlify's CDN can take a few seconds to finish propagating a new deploy across every edge
// POP. A reload that lands inside that window can 404 again immediately, so the second
// attempt gets a short head start to let propagation catch up.
const CHUNK_RELOAD_BACKOFF_MS = 1200;
const CHUNK_RETRY_PARAM = '_chunkRetry';

// This component sits inside the app's outermost Suspense, which resolves before the
// nested, lazy-loaded route chunks further down the tree have settled. The delay gives
// those nested chunks time to reject before the key is cleared; if one does, the single
// ErrorBoundary that wraps the whole app unmounts this component first, cancelling the
// timer via its cleanup so the attempt count survives into the next cycle.
const CHUNK_RELOAD_CLEANUP_DELAY_MS = 3000;

// The fallback is the app's last line of defense, so nothing here may throw: an error thrown by
// a boundary's fallback escapes to the next boundary up, and above the app's outermost one there
// is nothing to catch it. Storage access therefore goes through safeSessionStorage.
//
// The attempt count is kept in two places: sessionStorage, and the cache-busting URL param
// (`?_chunkRetry=<attempt>.<timestamp>`). The URL copy still counts when the browser blocks
// storage or rejects writes, so those users get the same reloads as everyone else.
const getUrlChunkReloadAttempts = (): number => {
    const value = new URLSearchParams(window.location.search).get(CHUNK_RETRY_PARAM);
    const attempts = Number.parseInt(value ?? '', 10);

    return Number.isNaN(attempts) ? 0 : attempts;
};

const getChunkReloadAttempts = (): number =>
    Math.max(Number(safeSessionStorage.get(CHUNK_RELOAD_KEY) ?? 0), getUrlChunkReloadAttempts());

// Also strips the URL copy, so a healthy session doesn't leave the param in the address bar and a
// manual reload after an exhausted budget starts a fresh one.
const clearChunkReloadAttempts = () => {
    safeSessionStorage.remove(CHUNK_RELOAD_KEY);

    if (window.location.search.includes(CHUNK_RETRY_PARAM)) {
        const url = new URL(window.location.href);
        url.searchParams.delete(CHUNK_RETRY_PARAM);
        window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
    }
};

/**
 * Place this component as a direct child inside the ErrorBoundary that wraps the app.
 */
export const ChunkReloadKeyCleanup = () => {
    useEffect(() => {
        const timer = setTimeout(clearChunkReloadAttempts, CHUNK_RELOAD_CLEANUP_DELAY_MS);

        return () => clearTimeout(timer);
    }, []);
    return null;
};

const isChunkLoadError = (error: unknown): boolean => {
    const message = error instanceof Error ? error.message : String(error);
    return CHUNK_LOAD_PATTERNS.some(pattern => pattern.test(message));
};

// An unchanged URL can be served from a stale cache by anything keyed on it (a corporate
// proxy, an intermediary that doesn't fully honor no-store), so a plain reload can hit the
// same stale index.html twice in a row. A unique URL guarantees no such cache already holds
// an entry for this exact request.
const reloadWithCacheBust = (attempt: number) => {
    const url = new URL(window.location.href);
    url.searchParams.set(CHUNK_RETRY_PARAM, `${attempt}.${Date.now()}`);
    window.location.replace(`${url.pathname}${url.search}${url.hash}`);
};

const ErrorFallback = ({ error }: Readonly<FallbackProps>) => {
    const isChunk = isChunkLoadError(error);
    const didAttemptReloadRef = useRef(false);

    // Captured once at mount so re-renders (e.g. parent state changes) never flip this
    // value after the effect has already changed the stored attempt count.
    const { current: reloadsExhausted } = useRef(isChunk && getChunkReloadAttempts() >= MAX_CHUNK_RELOAD_ATTEMPTS);

    useEffect(() => {
        if (didAttemptReloadRef.current) return;
        didAttemptReloadRef.current = true;

        if (isChunk) {
            const attempts = getChunkReloadAttempts();

            if (attempts < MAX_CHUNK_RELOAD_ATTEMPTS) {
                // Capturing an exception for observability trends. We use captureException
                // instead of addBreadcrumb to ensure the event and its breadcrumbs are
                // actually transmitted before the reload.
                Sentry.captureException(error, {
                    level: 'info',
                    tags: { chunk_load_auto_reload: true, chunk_load_attempt: attempts + 1 },
                });

                const nextAttempt = attempts + 1;
                safeSessionStorage.set(CHUNK_RELOAD_KEY, String(nextAttempt));

                // Ensure the event is flushed to Sentry before we reload the page and wipe memory
                void Sentry.flush(2000).finally(() => {
                    if (attempts === 0) {
                        reloadWithCacheBust(nextAttempt);
                    } else {
                        setTimeout(() => reloadWithCacheBust(nextAttempt), CHUNK_RELOAD_BACKOFF_MS);
                    }
                });
                return;
            }
            // Reload budget exhausted — reloading did not fix it.
            clearChunkReloadAttempts();
            Sentry.captureException(error, {
                level: 'warning',
                tags: { chunk_load_retry: true, chunk_load_attempts: attempts },
            });
        } else {
            clearChunkReloadAttempts();
            Sentry.captureException(error);
        }
    }, [error, isChunk]);

    if (isChunk && !reloadsExhausted) {
        return null;
    }

    if (isChunk) {
        return <GenericError message="A new version of the app is available. Please reload to continue." />;
    }

    const errorMessage = error instanceof Error ? error.message : String(error);

    return <GenericError message={errorMessage} />;
};

export default ErrorFallback;
